import DeleteIcon from "@mui/icons-material/Delete";
import { IconButton } from "@mui/material";
import { useAuthorizationServiceV1 } from "@rw1/api-services";
import { Authorization_V1 } from "@rw1/auto-generated-clients";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DataTable } from "../../../components/data-table/DataTable";
import { QueryKeys } from "../../../hooks/QueryKeys";
import { useAllRoles } from "../../../hooks/useAllRoles";
import { useCompaniesData } from "../../../hooks/useCompaniesData";
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useLocalization } from "../../../hooks/useLocalization";
import { useUserRoles } from "../../../hooks/useUserRoles";
import { GrantRoleDialog } from "./GrantRoleDialog";
import { RevokeRoleDialog } from "./RevokeRoleDialog";

interface RolesTabProps {
    userId: string;
}

export interface TUserRoleRow {
    roleId: string;
    roleName: string;
    organizationId?: string;
    companyName: string;
}

const getRoleColumns = (
    t: (key: string) => string,
    onRevokeRole: (row: TUserRoleRow) => void,
    showCompanyColumn: boolean,
    isActionBlocked: boolean
): ColumnDef<TUserRoleRow, any>[] => {
    const columns: ColumnDef<TUserRoleRow, any>[] = [
        {
            id: "roleName",
            header: t("users.roles.tableHeaders.role"),
            accessorKey: "roleName",
            enableSorting: true,
            enableColumnFilter: true,
            sortingFn: "text",
            size: 250,
        },
    ];

    if (showCompanyColumn) {
        columns.push({
            id: "companyName",
            header: t("users.roles.tableHeaders.organization"),
            accessorKey: "companyName",
            enableSorting: true,
            enableColumnFilter: true,
            sortingFn: "text",
            size: 250,
        });
    }

    columns.push({
        id: "actions",
        header: "",
        size: 50,
        enableSorting: false,
        cell: ({ row }) =>
            isActionBlocked ? null : (
                <IconButton
                    aria-label={t("common.delete")}
                    color="error"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRevokeRole(row.original);
                    }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                    }}
                    size="small"
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            ),
    });

    return columns;
};

const INITIAL_SORTING: SortingState = [{ id: "roleName", desc: false }];

export const RolesTab: React.FC<RolesTabProps> = ({ userId }) => {
    const { companyId } = useParams();
    const { t } = useLocalization();
    const { user: currentUser } = useCurrentUser();
    const queryClient = useQueryClient();
    const { userRoles, isLoading: isRolesLoading } = useUserRoles(userId);
    const { allRoles } = useAllRoles();
    const { companiesData, isLoading: isCompaniesLoading } = useCompaniesData({
        enabled: true,
    });
    const authorizationServiceV1 = useAuthorizationServiceV1();

    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
    const [revokeCtx, setRevokeCtx] = useState<TUserRoleRow | null>(null);

    const showCompanyColumn = useMemo(
        () => (companiesData?.length ?? 0) > 1,
        [companiesData]
    );
    const isActionBlocked = currentUser?.userId === userId;

    const data: TUserRoleRow[] = useMemo(() => {
        if (!userRoles) return [];

        return userRoles.map((x) => {
            const roleName =
                allRoles.find((y) => y.roleId === x.roleId)?.roleName ?? x.roleId;
            const companyName = x.organizationId
                ? companiesData?.find((c) => c.companyId === x.organizationId)
                    ?.friendlyName ?? x.organizationId
                : t("users.roles.global");

            return {
                roleId: x.roleId,
                roleName,
                organizationId: x.organizationId,
                companyName,
            };
        });
    }, [userRoles, allRoles, companiesData, t]);

    const refreshRoles = useCallback(async () => {
        await queryClient.invalidateQueries({
            queryKey: [QueryKeys.UserRoles, userId],
        });
        await queryClient.invalidateQueries({
            queryKey: [QueryKeys.OrganizationUserRoles, companyId],
        });
    }, [queryClient, userId, companyId]);

    const handleGrantRole = async (
        roleId: string,
        organizationId?: string
    ) => {
        await authorizationServiceV1.grantUserRole(
            userId,
            new Authorization_V1.RoleGrant({
                roleId,
                organizationId,
            })
        );
        await refreshRoles();
    };

    const handleOpenRevokeModal = useCallback((row: TUserRoleRow) => {
        setRevokeCtx(row);
    }, []);

    const columns = useMemo(
        () => getRoleColumns(t, handleOpenRevokeModal, showCompanyColumn, isActionBlocked),
        [t, handleOpenRevokeModal, showCompanyColumn, isActionBlocked]
    );

    return (
        <>
            <DataTable<TUserRoleRow>
                actionButtonText={t("users.roles.grantRole")}
                columns={columns}
                data={data}
                defaultSort={INITIAL_SORTING}
                globalFilterPlaceholder={t("users.roles.searchRoles")}
                isLoading={isRolesLoading || isCompaniesLoading}
                onActionClick={() => setIsAssignDialogOpen(true)}
                showActionButton={!isActionBlocked}
            />

            <GrantRoleDialog
                assignedRoles={userRoles}
                isOpen={isAssignDialogOpen}
                onClose={() => setIsAssignDialogOpen(false)}
                onGrantRole={handleGrantRole}
            />

            <RevokeRoleDialog
                ctx={revokeCtx}
                onClose={() => setRevokeCtx(null)}
                open={Boolean(revokeCtx)}
                userId={userId}
            />
        </>
    );
};